import React, { useEffect, useState } from 'react'
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Fade,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
} from '@mui/material'

import type { TypographyProps } from '@mui/material/Typography'
import LawTechLogo from '../../assets/images/Lawttorney Logo.jpeg'
import MenuIcon from '@mui/icons-material/Menu'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'

import { styled } from '@mui/material/styles'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useAuthContext, useSidebarContext } from '../../contexts'

// ------------------------------------------
// Guest Links (with external links)
const guestLinks = [
  { label: 'Home', path: '/', external: false },
  { label: 'Contact Us', path: '/contact-us', external: false },
  { label: 'About Us', path: 'https://www.lawttorney.com/about', external: true },
  { label: 'Blog', path: 'https://www.lawttorney.com/blogs', external: true },
  {
    label: 'Join Affiliate Program',
    path: 'https://worlddelopmentcorporation.zohothrive.in/thrive/publicpages/affiliate-registration/worlddelopmentcorporation/df8373a15efb689e929e0583bc43642194d58abd37f1c2be61af874d312d6fe6?utm_source=website&utm_medium=referal&utm_campaign=affilaite program-2&utm_term=legal',
    external: true,
  },
]

const userLinks = [
  { label: 'Home', path: '/newChat', external: false },
  { label: 'Verifier', path: '/verifier', external: false },
  { label: 'Editor', path: '/editor', external: false },
  { label: 'Contact Us', path: '/contact-us', external: false },
  { label: 'Blog', path: 'https://www.lawttorney.com/blogs', external: true },
]

// Hover animated Typography
interface NavTextProps extends TypographyProps {
  to?: string
  component?: React.ElementType
}

const NavText = styled((props: NavTextProps) => <Typography {...props} />)(({ theme }) => ({
  cursor: 'pointer',
  position: 'relative',
  transition: 'color 0.3s ease',
  whiteSpace: 'nowrap',
  fontSize: '1rem',
  fontWeight: 500,
  '&:hover': { color: theme.palette.primary.main },
  '&:after': {
    content: '""',
    position: 'absolute',
    bottom: -2,
    left: 0,
    width: '0%',
    height: '2px',
    backgroundColor: theme.palette.primary.main,
    transition: 'width 0.3s ease',
  },
  '&:hover:after': { width: '100%' },
}))

// ------------------------------------------

export const NavLinks: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()

  const [open, setOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)

  const [showProfileMenu, setShowProfileMenu] = useState(false)

  const token = sessionStorage.getItem('token')
  const firstName = sessionStorage.getItem('firstName') ?? ''
  const lastName = sessionStorage.getItem('lastName') ?? ''
  const email = sessionStorage.getItem('email') ?? 'user@example.com'

  const { uniqueString, setIsLoggedIn, deleteUniqueString } = useSidebarContext()
  const { setAuthenticated } = useAuthContext()

  const isLoggedIn = Boolean(token)

  const initials = (firstName?.[0] ?? '').toUpperCase() + (lastName?.[0] ?? '').toUpperCase()

  const isActive = (path: string) => location.pathname === path

  // Avatar dropdown
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const menuOpen = Boolean(anchorEl)

  const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const closeMenu = () => {
    setAnchorEl(null)
  }

  // Logout modal
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false)

  const openLogoutDialog = () => {
    closeMenu()
    setLogoutDialogOpen(true)
  }

  const confirmLogout = () => {
    setIsLoggedIn(false)
    setAuthenticated(false)
    if (uniqueString) {
      deleteUniqueString(uniqueString)
    }
    sessionStorage.clear()
    setLogoutDialogOpen(false)
    navigate('/auth/login', { replace: true })
  }

  const cancelLogout = () => {
    setLogoutDialogOpen(false)
  }

  // Scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20)
    }
    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const toggleDrawer = (val: boolean) => () => {
    setOpen(val)
  }

  return (
    <>
      {/* NAVBAR */}
      <AppBar
        style={{ zIndex: 10002 }}
        position='fixed'
        elevation={scrolled ? 3 : 0}
        sx={{
          backgroundColor: 'rgba(255, 255, 255, 1)',
          // backgroundColor: scrolled ? 'rgba(255, 255, 255, 1)' : 'transparent',
          backdropFilter: scrolled ? 'blur(6px)' : 'none',
          transition: 'all 0.3s ease',
        }}
      >
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
          {/* LOGO */}
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
            onClick={() => {
              token ? navigate('/newChat') : navigate('/')
            }}
          >
            <img src={LawTechLogo} alt='Logo' style={{ height: '75px', borderRadius: 4 }} />
          </Box>

          {/* DESKTOP MENU */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 4 }}>
            {(isLoggedIn ? userLinks : guestLinks).map((link) =>
              link.external ? (
                <NavText
                  key={link.label}
                  variant='body1'
                  onClick={() => window.open(link.path, '_blank', 'noopener,noreferrer')}
                  sx={{
                    color: 'inherit',
                  }}
                >
                  {link.label}
                </NavText>
              ) : (
                <NavText
                  key={link.label}
                  variant='body1'
                  component={Link}
                  to={link.path}
                  sx={{
                    textDecoration: 'none',
                    color: isActive(link.path) ? 'primary.main' : 'inherit',
                    '&:after': {
                      width: isActive(link.path) ? '100%' : '0%',
                    },
                  }}
                >
                  {link.label}
                </NavText>
              ),
            )}

            {/* PROFILE BUTTON */}
            {isLoggedIn ? (
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
                onClick={handleAvatarClick}
              >
                <Avatar sx={{ bgcolor: '#587dbd', fontSize: '1rem' }}>{initials}</Avatar>

                <KeyboardArrowDownIcon
                  sx={{
                    transition: 'transform 0.3s ease',
                    transform: menuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                />
              </Box>
            ) : (
              <Button variant='contained' component={Link} to='/auth/login'>
                Login
              </Button>
            )}
          </Box>

          {/* MOBILE MENU ICON */}
          <IconButton sx={{ display: { xs: 'block', md: 'none' } }} onClick={toggleDrawer(true)}>
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* PROFILE MENU */}
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={closeMenu}
        TransitionComponent={Fade}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {/* USER INFO HEADER */}
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography fontWeight={600}>
            {firstName} {lastName}
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            {email}
          </Typography>
        </Box>

        <Divider />

        <MenuItem
          sx={{ fontSize: '1rem' }}
          onClick={() => {
            navigate('/faq/need-help-with-a-billing-issue')
          }}
        >
          <HelpOutlineIcon sx={{ mr: 2 }} /> Help & FAQs
        </MenuItem>

        <MenuItem
          sx={{ fontSize: '1rem' }}
          onClick={() => {
            navigate('/settings')
          }}
        >
          <SettingsOutlinedIcon sx={{ mr: 2 }} /> Settings
        </MenuItem>

        <Divider />

        <MenuItem sx={{ fontSize: '1rem' }} onClick={openLogoutDialog}>
          <LogoutOutlinedIcon sx={{ mr: 2 }} /> Logout
        </MenuItem>
      </Menu>

      {/* LOGOUT CONFIRM MODAL */}
      <Dialog open={logoutDialogOpen} onClose={cancelLogout}>
        <DialogTitle>Logout</DialogTitle>

        <DialogContent>
          <Typography>Are you sure you want to logout?</Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={cancelLogout}>Cancel</Button>
          <Button color='error' onClick={confirmLogout}>
            Logout
          </Button>
        </DialogActions>
      </Dialog>

      {/* MOBILE DRAWER */}
      <Drawer anchor='right' open={open} onClose={toggleDrawer(false)}>
        <Box sx={{ width: 250, marginTop: '72px' }}>
          <List>
            {/* Normal links */}
            {(isLoggedIn ? userLinks : guestLinks).map((link) => (
              <Collapse in={open} timeout={300} unmountOnExit key={link.label}>
                <ListItem disablePadding>
                  {link.external ? (
                    <ListItemButton
                      onClick={() => {
                        window.open(link.path, '_blank', 'noopener,noreferrer')
                      }}
                    >
                      <ListItemText
                        primary={link.label}
                        primaryTypographyProps={{ fontSize: '1rem' }}
                      />
                    </ListItemButton>
                  ) : (
                    <ListItemButton
                      onClick={() => {
                        navigate(link.path)
                        setOpen(false)
                      }}
                    >
                      <ListItemText
                        primary={link.label}
                        primaryTypographyProps={{ fontSize: '1rem' }}
                      />
                    </ListItemButton>
                  )}
                </ListItem>
              </Collapse>
            ))}

            {/* PROFILE SECTION FOR MOBILE */}
            {isLoggedIn && (
              <>
                <ListItem
                  disablePadding
                  onClick={() => {
                    setShowProfileMenu((prev) => !prev)
                  }}
                >
                  <ListItemButton>
                    <Avatar sx={{ bgcolor: '#587dbd', fontSize: '1rem', mr: 1 }}>{initials}</Avatar>
                    <ListItemText primary='Profile' />
                    <KeyboardArrowDownIcon
                      sx={{
                        transition: 'transform 0.3s ease',
                        transform: showProfileMenu ? 'rotate(180deg)' : 'rotate(0deg)',
                      }}
                    />
                  </ListItemButton>
                </ListItem>

                <Collapse in={showProfileMenu} timeout={250} unmountOnExit>
                  <List component='div' disablePadding>
                    <ListItemButton
                      sx={{ pl: 4 }}
                      onClick={() => {
                        navigate('/settings')
                        setOpen(false)
                      }}
                    >
                      <SettingsOutlinedIcon sx={{ mr: 2 }} />
                      <ListItemText primary='Settings' />
                    </ListItemButton>

                    <ListItemButton
                      sx={{ pl: 4 }}
                      onClick={() => {
                        navigate('/faq/need-help-with-a-billing-issue')
                        setOpen(false)
                      }}
                    >
                      <HelpOutlineIcon sx={{ mr: 2 }} />
                      <ListItemText primary='Help & FAQs' />
                    </ListItemButton>

                    <ListItemButton
                      sx={{ pl: 4 }}
                      onClick={() => {
                        openLogoutDialog()
                      }}
                    >
                      <LogoutOutlinedIcon sx={{ mr: 2 }} />
                      <ListItemText primary='Logout' />
                    </ListItemButton>
                  </List>
                </Collapse>
              </>
            )}

            {!isLoggedIn && (
              <ListItem>
                <Button
                  fullWidth
                  variant='contained'
                  onClick={() => {
                    navigate('/auth/login')
                    setOpen(false)
                  }}
                >
                  Login
                </Button>
              </ListItem>
            )}
          </List>
        </Box>
      </Drawer>
    </>
  )
}
